import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { getBubblePosition, getBubbleRadius, getQuadrant } from '../utils/opportunityMatrix';

export const OpportunityMatrix = ({ opportunities }) => {
  // Filter only rated opportunities
  const ratedOpportunities = opportunities.filter(opp => opp.impact > 0 && opp.effort > 0);
  
  if (ratedOpportunities.length === 0) {
    return null;
  }
  
  // Chart dimensions
  const width = 600;
  const height = 600;
  const padding = 80; // Increased for labels outside
  const chartWidth = width - 2 * padding;
  const chartHeight = height - 2 * padding;
  const reduceMotion = useReducedMotion();
  
  // Quadrant colors - Quick Wins bright yellow
  const quadrants = [
    { x: 0, y: 0, width: chartWidth / 2, height: chartHeight / 2, label: 'Quick Wins', color: 'rgb(217 229 5)', opacity: 0.3 },
    { x: chartWidth / 2, y: 0, width: chartWidth / 2, height: chartHeight / 2, label: 'Strategic Plays', color: 'rgb(28 51 74)', opacity: 0.08 },
    { x: 0, y: chartHeight / 2, width: chartWidth / 2, height: chartHeight / 2, label: 'Fill-Ins', color: 'rgb(228 228 231)', opacity: 0.15 },
    { x: chartWidth / 2, y: chartHeight / 2, width: chartWidth / 2, height: chartHeight / 2, label: 'Time Sinks', color: 'rgb(209 213 219)', opacity: 0.2 }
  ];
  
  return (
    <div className="w-full flex justify-center py-8">
      <div className="relative">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-[600px] border border-border rounded-lg bg-background shadow-sm">
          {/* Quadrant backgrounds */}
          {quadrants.map((quad, idx) => (
            <rect
              key={idx}
              x={padding + quad.x}
              y={padding + quad.y}
              width={quad.width}
              height={quad.height}
              fill={quad.color}
              opacity={quad.opacity}
            />
          ))}
          
          {/* Center divider lines */}
          <line
            x1={padding + chartWidth / 2}
            y1={padding}
            x2={padding + chartWidth / 2}
            y2={height - padding}
            stroke="currentColor"
            strokeWidth="2"
            opacity="0.3"
            className="text-border"
          />
          <line
            x1={padding}
            y1={padding + chartHeight / 2}
            x2={width - padding}
            y2={padding + chartHeight / 2}
            stroke="currentColor"
            strokeWidth="2"
            opacity="0.3"
            className="text-border"
          />
          
          {/* Axes */}
          <line
            x1={padding}
            y1={height - padding}
            x2={width - padding}
            y2={height - padding}
            stroke="currentColor"
            strokeWidth="2"
            className="text-foreground"
          />
          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={height - padding}
            stroke="currentColor"
            strokeWidth="2"
            className="text-foreground"
          />
          
          {/* Quadrant labels - centered in each quadrant */}
          <text x={padding + chartWidth * 0.25} y={padding + chartHeight * 0.25} textAnchor="middle" className="text-sm font-bold fill-secondary pointer-events-none">
            Quick Wins
          </text>
          <text x={padding + chartWidth * 0.75} y={padding + chartHeight * 0.25} textAnchor="middle" className="text-sm font-bold fill-primary pointer-events-none">
            Strategic Plays
          </text>
          <text x={padding + chartWidth * 0.25} y={padding + chartHeight * 0.75} textAnchor="middle" className="text-sm font-bold fill-muted-foreground pointer-events-none">
            Fill-Ins
          </text>
          <text x={padding + chartWidth * 0.75} y={padding + chartHeight * 0.75} textAnchor="middle" className="text-sm font-bold fill-gray-500 pointer-events-none">
            Time Sinks
          </text>
          
          {/* Axis labels - Low/High only */}
          <text
            x={padding + 20}
            y={height - padding + 35}
            textAnchor="start"
            className="text-xs text-muted-foreground fill-muted-foreground"
          >
            Low
          </text>
          <text
            x={width - padding - 20}
            y={height - padding + 35}
            textAnchor="end"
            className="text-xs text-muted-foreground fill-muted-foreground"
          >
            High
          </text>
          <text
            x={width / 2}
            y={height - padding + 35}
            textAnchor="middle"
            className="text-sm font-semibold fill-foreground"
          >
            Implementation Difficulty
          </text>
          
          <text
            x={padding - 45}
            y={height - padding - 20}
            textAnchor="start"
            className="text-xs text-muted-foreground fill-muted-foreground"
          >
            Low
          </text>
          <text
            x={padding - 45}
            y={padding + 20}
            textAnchor="start"
            className="text-xs text-muted-foreground fill-muted-foreground"
          >
            High
          </text>
          <text
            x={15}
            y={height / 2}
            textAnchor="middle"
            transform={`rotate(-90, 15, ${height / 2})`}
            className="text-sm font-semibold fill-foreground"
          >
            Business Impact
          </text>
          
          {/* Plot opportunities with deterministic positions */}
          {ratedOpportunities.map((opp, idx) => {
            const { cx, cy } = getBubblePosition(opp, { width, height, padding });
            const radius = getBubbleRadius(opp.annual);
            const quadrant = getQuadrant(opp);
            const fillColor = quadrant.color;
            
            return (
              <motion.g
                key={idx}
                initial={reduceMotion ? false : { opacity: 0, scale: 0.75 }}
                animate={reduceMotion ? false : { opacity: 1, scale: 1 }}
                transition={{ delay: 0.04 * idx, duration: 0.25 }}
              >
                <circle
                  cx={cx}
                  cy={cy}
                  r={radius}
                  fill={fillColor}
                  opacity="0.85"
                  stroke="white"
                  strokeWidth="2.5"
                  className="cursor-pointer hover:opacity-100 transition-opacity"
                >
                  <title>{`${opp.name}\nImpact: ${opp.impact} | Difficulty: ${opp.effort}\n$${opp.annual.toLocaleString()}/year`}</title>
                </circle>
              </motion.g>
            );
          })}
        </svg>
        
        {/* Legend */}
        <div className="mt-6 space-y-2">
          <p className="text-xs text-muted-foreground text-center">
            Hover over bubbles for full opportunity details and annual value
          </p>
          <div className="flex justify-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(217 229 5)' }}></div>
              <span>Quick Wins</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(28 51 74)' }}></div>
              <span>Strategic</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(156 163 175)' }}></div>
              <span>Fill-Ins</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(107 114 128)' }}></div>
              <span>Time Sinks</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
